import React, {useEffect, useState} from "react";
import {useDarkMode} from "../../contexts/darkModeContext";
import {
    Button,
    Dialog,
    DialogBackdrop,
    DialogPanel,
    DialogTitle,
    Field,
    Input,
    Label,
    Select,
    Switch
} from "@headlessui/react";
import {
    AdjustmentsHorizontalIcon,
    ArrowPathIcon, CalendarDaysIcon,
    DocumentCurrencyDollarIcon,
    PhotoIcon,
    PlusCircleIcon,
    SquaresPlusIcon,
    TrashIcon,
    XCircleIcon
} from "@heroicons/react/24/outline";
import {collection, deleteDoc, deleteField, doc, getDocs, updateDoc} from "firebase/firestore";
import {deleteObject, getDownloadURL, ref, uploadBytes} from "firebase/storage";
import {db, storage} from "../../firebase/firebase";
import Loading from "../Widgets/Loading";
import {
    categoryColors, formatFirestoreDateShort,
    formatTimeStampDate,
} from "../GLOBAL/GlobalFunctions";
import UMIcons from "../Widgets/UMIcons";

const PricingCard = ({ product, user }) => {

    const { darkMode } = useDarkMode();
    
    const [alertsOn] = useState(() => {
        const storedSettings = localStorage.getItem('alertsOn');
        return storedSettings ? JSON.parse(storedSettings).alertsOn : false; // Default to false if null
    });
    
    const [name, setName] = useState(product.name || '');
    const [ph, setPh] = useState(parseFloat(product.ph).toFixed(2) || '');
    const [weight, setWeight] = useState(product.weight || '');
    const [GPT, setGPT] = useState(product.GPT || '');
    const [category, setCategory] = useState(product.category || '');
    const [sds, setSDS] = useState(product.sds || '');
    const [promotionalImageUrl, setPromotionalImageUrl] = useState(product.promotionalImageUrl || '');
    const [promotionalImagePath, setPromotionalImagePath] = useState(product.promotionalImagePath || '');
    const [imageUpdating, setImageUpdating] = useState(false);
    
    const [lock, setLock] = useState(product.lock || false);
    const [AG, setAG] = useState(product.AG || false);
    const [PLAIF, setPLAIF] = useState(product.PLAIF || false);
    const [WEB, setWEB] = useState(product.web || false);
    
    const [BULK, setBulk] = useState(product.BULK || false);
    const [BULKCODE, setBULKCODE] = useState(product.BULKCODE || false);

    useEffect(() => {
        setIsLoaded(true);
    }, []);

    const [isLoaded, setIsLoaded] = useState(false); // State to track when the component is loaded
    
    const [prices, setPrices] = useState(product.prices);
    const [isModified, setIsModified] = useState(false);

    // Function to handle changes in inputs
    const handleInputChange = (index, field, value) => {
        const updatedPrices = prices.map((price, i) =>
            i === index ? { ...price, [field]: value } : price
        );
        setPrices(updatedPrices);
        setIsModified(true);
    };
    

    // Function to handle changes in name
    const handleNameChange = (e) => {
        setName(e.target.value);
        setIsModified(true); // Mark as modified
    };

    // Function to handle changes in ph
    const handlePHChange = (e) => {
        setPh(e.target.value);
        setIsModified(true); // Mark as modified
    };
    
     // Function to handle changes in sds
    const handleSDSChange = (e) => {
        setSDS(e.target.value);
        setIsModified(true); // Mark as modified
    };

    // Function to handle changes in weight
    const handleWeightChange = (e) => {
        setWeight(e.target.value);
        setIsModified(true); // Mark as modified
    };
    
    // Function to handle changes in weight
    const handleGPTChange = (e) => {
        setGPT(e.target.value);
        setIsModified(true); // Mark as modified
    };

    // Function to handle changes in weight
    const handleCategoryChange = (e) => {
        setCategory(e.target.value);
        setIsModified(true); // Mark as modified
    };
    const [updating, setUpdating] = useState(false);

    const markProductsUpdated = async () => {
        const usersSnapshot = await getDocs(collection(db, "Users"));
        await Promise.all(usersSnapshot.docs.map((userDoc) =>
            updateDoc(doc(db, "Users", userDoc.id), { productUpdated: true })
        ));
    };

    const handlePromotionalImageUpload = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert('Please select an image file.');
            return;
        }

        setImageUpdating(true);
        const previousPath = promotionalImagePath;
        try {
            const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '-');
            const imagePath = `productImages/promotional/${product.id}/${Date.now()}-${safeFileName}`;
            const imageRef = ref(storage, imagePath);
            const snapshot = await uploadBytes(imageRef, file);
            const imageUrl = await getDownloadURL(snapshot.ref);

            await updateDoc(doc(db, "Products", product.id), {
                promotionalImageUrl: imageUrl,
                promotionalImagePath: imagePath,
                updated: Date.now(),
                updatedBy: user,
            });

            setPromotionalImageUrl(imageUrl);
            setPromotionalImagePath(imagePath);
            await markProductsUpdated();

            if (previousPath && previousPath !== imagePath) {
                try {
                    await deleteObject(ref(storage, previousPath));
                } catch (deleteError) {
                    console.warn('The previous promotional image could not be removed:', deleteError);
                }
            }
        } catch (error) {
            console.error('Error uploading promotional image:', error);
            alert('The promotional image could not be uploaded. Please try again.');
        } finally {
            setImageUpdating(false);
        }
    };

    const handlePromotionalImageDelete = async () => {
        if (!promotionalImageUrl) return;

        setImageUpdating(true);
        try {
            await updateDoc(doc(db, "Products", product.id), {
                promotionalImageUrl: deleteField(),
                promotionalImagePath: deleteField(),
                updated: Date.now(),
                updatedBy: user,
            });

            if (promotionalImagePath) {
                try {
                    await deleteObject(ref(storage, promotionalImagePath));
                } catch (deleteError) {
                    console.warn('The promotional image file could not be removed:', deleteError);
                }
            }

            setPromotionalImageUrl('');
            setPromotionalImagePath('');
            await markProductsUpdated();
        } catch (error) {
            console.error('Error deleting promotional image:', error);
            alert('The promotional image could not be deleted. Please try again.');
        } finally {
            setImageUpdating(false);
        }
    };

    // Function to save updated data to Firebase
    const handleSave = async () => {
        setUpdating(true);
        const startTime = Date.now();
        
        try {
            const productDocRef = doc(db, "Products", product.id); // Replace `product.id` with the actual document ID
            await updateDoc(productDocRef, {
                prices,
                name: name,
                ph: parseFloat(ph),
                weight: parseFloat(weight),
                category: category,
                sds: sds,
                lock: lock,
                AG: AG,
                GPT: GPT,
                PLAIF: PLAIF,
                updated: Date.now(),
                updatedBy: user,
            });
            console.log("Product updated successfully!");

            // Update the productUpdated field in all documents in the Users collection
            const usersCollectionRef = collection(db, "Users");
            const usersSnapshot = await getDocs(usersCollectionRef);

            const updatePromises = usersSnapshot.docs.map((userDoc) => {
                const userDocRef = doc(db, "Users", userDoc.id);
                return updateDoc(userDocRef, { productUpdated: true });
            });

            await Promise.all(updatePromises); // Wait for all updates to complete
            console.log("Updated productUpdated field for all users!");

            if (alertsOn) {
                alert(`${name} Updated successfully!`);
            }

            setIsModified(false);
        } catch (error) {
            console.error("Error updating product or users:", error);
        }
        
        const endTime = Date.now();
        const timeElapsed = (endTime - startTime) /1000;
        setUpdating(false);
        if (alertsOn) {
        alert(`Product Updated in ${timeElapsed} secs`);
        }
    };


    // Add a new price entry to the prices array
    const handleAddPrice = () => {
        const newPrice = {
            um: '',
            code: '',
            ne: '',
            se: '',
            ag: '',
            dist1: '',
            dist2: ''
        };
        setPrices([...prices, newPrice]);
        setIsModified(true);
    };

    // Remove a price entry from the prices array
    const handleRemovePrice = (index) => {
        const updatedPrices = prices.filter((_, i) => i !== index);
        setPrices(updatedPrices);
        setIsModified(true);
    };

    const bgColor = categoryColors.find(c => c.category === category)?.color || '';

    const [showId, setShowId] = useState(false);
    const [clickCount, setClickCount] = useState(0);

    const handleTripleClick = () => {
        setClickCount(prevCount => prevCount + 1);
        setTimeout(() => setClickCount(0), 500); // Reset click count after 500ms

        if (clickCount === 2) { // On the third click
            setShowId(true); // Set showId to true
            setTimeout(() => setShowId(false), 15000); // Reset showId to false after 30 seconds
        }
    };
    
    const [isDeleting, setIsDeleting] = useState(false);
    
    
    const handleDeleteProduct = async (productID) => {
        // Check if clientId and orderId are not empty
        if (!productID) {
            console.warn("Product ID is missing. Delete operation aborted.");
            return;
        }

        try {
            const orderRef = doc(db, `Products`, productID);
            await deleteDoc(orderRef);
            console.log(`Product with ID ${productID} deleted successfully`);
            
            
            
            // Update the productUpdated field in all documents in the Users collection
            const usersCollectionRef = collection(db, "Users");
            const usersSnapshot = await getDocs(usersCollectionRef);

            const updatePromises = usersSnapshot.docs.map((userDoc) => {
                const userDocRef = doc(db, "Users", userDoc.id);
                return updateDoc(userDocRef, { productUpdated: true });
            });

            await Promise.all(updatePromises); // Wait for all updates to complete
            console.log("Updated productUpdated field for all users!");
            
            
            
            setIsDeleting(false);
            // on();
        
        } catch (error) {
            console.error("Error deleting order:", error);
        }
    };
    
    
    const [open, setOpen] = useState(false);


    return (
        <div className={`w-full flex flex-col hover:bg-opacity-90  ${darkMode ? 'border-neutral-700' : ''} transition-transform duration-500 ease-in-out ${isLoaded ? '' : 'translate-x-full opacity-0'}`}>

            {updating && (
                <Loading/>
            )}
            
            {/*HEADER*/}
            <div className="w-full flex flex-row py-3 px-2 gap-x-2">
                
                <div className="relative w-full grid grid-cols-2">
                    
                    {/*Main*/}
                    <div className="col-span-1 gap-x-2 flex flex-row items-center h-full mt-auto mb-auto">
                        
                        <Button onClick={() => setIsDeleting(true)} className={` hover:animate-heartbeat`}>
                            <TrashIcon className={`size-5 text-red-600`}/>
                        </Button>
                        
                        {/*Category*/}
                        <div className={`w-64 p-1 py-1.5 items-center flex pl-3 ${bgColor} bg-opacity-80 rounded-lg hover:scale-105`}
                             onClick={handleTripleClick}>
                            <Select
                                id="category"
                                className={`rounded-lg text-center text-xs block w-full bg-transparent text-white line-clamp-1`}
                                value={category}
                                onChange={handleCategoryChange}
                            >
                                <option value="">Select Category...</option>
                                <option value="Biostimulants">Biostimulants</option>
                                <option value="Liquid Fertilizer">Liquid Fertilizers</option>
                                <option value="Dry Fertilizer">Dry Fertilizers</option>
                                <option value="Secondary Nutrients">Secondary Nutrients</option>
                                <option value="Micronutrients">Micronutrients</option>
                                <option value="Penetrants & Water Holding Agents">Penetrants & Water Holding Agents</option>
                                <option value="A-Plus® Solubilizing Surfactants">A-Plus® Solubilizing Surfactants</option>
                                <option value="Turfgrass Pigment">Turfgrass Pigment</option>
                                <option value="Water Treatments">Water Treatments</option>
                                <option value="Soil Amendments">Soil Amendments</option>
                                <option value="Fungicides">Fungicides</option>
                                <option value="Equipment">Equipment</option>
                                <option value="Test">Test</option>
                                <option value="Seed">Seed</option>
                                <option value="Promotional Item">Promotional Item</option>
                            </Select>
                        </div>
                        
                        {showId && (
                            <div className="text-xxs ml-3">
                                {product.id}
                            </div>
                        )}
                        
                        {/*Name*/}
                        <div className="justify-center w-full flex flex-col h-full">
                            <Input
                                className={`text-sm border-b focus:border-green-600 ${darkMode ? 'border-neutral-700' : ''} line-clamp-1 bg-transparent focus:outline-none py-1.5 w-full ${darkMode ? 'text-white' : 'text-black'}`}
                                type="string"
                                placeholder="Product Code"
                                value={name}
                                onChange={handleNameChange}
                            />
                        </div>
                    
                    </div>
                    
                    
                    {/*Update + Options*/}
                    <div className={`col-span-1 flex items-center justify-end gap-x-6`}>
                        
                        {/*Update Button*/}
                        
                        <div className={`${product.updatedBy ? '' : 'hidden'} text-gray-500 text-xs flex items-center  gap-x-1.5`}>
                            <CalendarDaysIcon className={`size-5`}/>
                            <div className={`${product.updatedBy ? '' : 'hidden'}`}>{product.updatedBy || ""} edited:</div>
                            <div className={``}>
                                {product.updatedAt
                                    ? formatFirestoreDateShort(product.updatedAt)
                                    : product.updated
                                        ? formatTimeStampDate(product.updated)
                                        : ""
                                }
                            </div>
                        </div>
                        
                        <div className="animate-rubberBand flex items-center">
                            <Button
                                className={`bg-blue-600 ${isModified ? 'hover:bg-opacity-50 hover:scale-105' : 'bg-opacity-50 text-opacity-50'} flex items-center text-white text-xs font-bold py-1 px-2 gap-x-2 rounded-lg`}
                                onClick={handleSave}
                            >
                                <ArrowPathIcon className={`size-4 stroke-2`}/>
                                {isModified ? 'Update' : 'Disabled'}
                            </Button>
                        </div>
                        
                        <Button onClick={() => setOpen(true)} className={`text-green-600`}>
                            <AdjustmentsHorizontalIcon className={`size-6`}/>
                        </Button>
                        
                    </div>
                
                </div>
            
            </div>

            {category === 'Promotional Item' && (
                <div className={`mx-2 mb-3 flex items-center gap-4 rounded-lg border p-3 ${darkMode ? 'border-neutral-700 bg-darkBgColor' : 'bg-gray-50'}`}>
                    <div className={`flex size-24 shrink-0 items-center justify-center overflow-hidden rounded-lg border ${darkMode ? 'border-neutral-700' : 'border-gray-200'}`}>
                        {promotionalImageUrl ? (
                            <img src={promotionalImageUrl} alt={`${name} promotional item`} className="size-full object-contain" />
                        ) : (
                            <PhotoIcon className="size-10 text-gray-400" />
                        )}
                    </div>
                    <div className="flex flex-col gap-2">
                        <div>
                            <div className="text-sm font-semibold">Promotional product image</div>
                            <div className="text-xs text-gray-500">Upload an image to display on this product's catalog card.</div>
                        </div>
                        <div className="flex items-center gap-2">
                            <label className={`cursor-pointer rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-500 ${imageUpdating ? 'pointer-events-none opacity-50' : ''}`}>
                                {imageUpdating ? 'Updating...' : promotionalImageUrl ? 'Replace image' : 'Upload image'}
                                <input type="file" accept="image/*" className="sr-only" onChange={handlePromotionalImageUpload} disabled={imageUpdating} />
                            </label>
                            {promotionalImageUrl && (
                                <Button type="button" onClick={handlePromotionalImageDelete} disabled={imageUpdating} className="flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-500/10 disabled:opacity-50">
                                    <TrashIcon className="size-4" /> Delete image
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            {/*Prices Table*/}
            <div className={`w-full flex flex-col border-y ${darkMode ? `${PLAIF ? 'border-blue-400 border-dashed' : 'border-neutral-700'} bg-darkMainColor` : `bg-white ${PLAIF ? 'border-blue-600' : ''}`}`}>
                
                <div className={`w-full relative grid grid-cols-7 py-0.5 gap-x-2 text-center items-center text-gray-500 text-xs border-b bg-gray-500/10 rounded-t-md ${darkMode ? 'border-neutral-700' : ''}`}>
                    <div className={`col-span-1 flex items-center justify-center gap-x-3`}>{BULK ? 'RATE' : 'UM'}
                        <Button className={`text-green-600 hover:scale-105 stroke-2 hover:animate-pulse`}
                            onClick={handleAddPrice}>
                            <PlusCircleIcon className="size-5"/>
                        </Button>
                    </div>
                    <div className={`col-span-1`}>Code</div>
                    <div className={`col-span-1`}>NE</div>
                    <div className={`col-span-1`}>SE</div>
                    <div className={`col-span-1 ${AG ? 'font-extrabold animate-pulse text-green-600' : ''}`}>AG</div>
                    <div className={`col-span-1`}>DIST1</div>
                    <div className={`col-span-1`}>DIST2</div>
                    <div className={`absolute left-1 flex items-center justify-center rounded-md gap-x-1 bg-green-600 size-4`}>
                        <span className={`text-white`}>{product.prices.length}</span>
                    </div>
                </div>

                <div className={``}>
                    {prices.length === 0 ? (
                        // Display custom dialog if sprays array is empty
                        <div className="flex w-full h-full justify-center items-center py-5">
                            
                            <div className={`flex items-center border-2 border-dashed ${darkMode ? 'text-white border-borderColor' : 'text-black bg-neutral-50'} w-fit px-24 h-fit mr-auto ml-auto justify-center rounded-lg hover:scale-105`}>
                                <div className="p-2 rounded-md shadow-gray-900 flex items-center justify-center flex-col m-2">
                                    <SquaresPlusIcon className={`w-6 stroke-1 `}/>
                                    <p className="text-md line-clamp-1 tracking-widest flex">No Pricing Available</p>
                                </div>
                            </div>
                        
                        </div>
                    
                    ) : (
                        // Display SprayCard components if sprays array is not empty
                        <div className={`divide-y ${darkMode ? 'divide-neutral-700' : ''}`}>
                            {prices.map((price, index) => {
                                return (
                                    
                                    
                                    <div key={index} className={``}>
                                        
                                        
                                        <div className="flex flex-row w-full items-center">
                                            
                                            {/*Fields Grid*/}
                                            <div className={`w-full grid text-xs grid-cols-7 divide-x ${darkMode ? 'divide-neutral-700' : ''}`}>
                                                
                                                {/*U/M*/}
                                                <div className={`relative col-span-1 flex items-center pl-1`}>
                                                    
                                                    <UMIcons selectedVolume={price.um} size={'size-5'} color={'text-gray-500'}/>
                                                    
                                                    <Select
                                                        type="text"
                                                        name="um"
                                                        value={price.um}
                                                        onChange={(e) => handleInputChange(index, 'um', e.target.value)}
                                                        className={`${BULK ? 'hidden' : ''} block w-full rounded-md ${darkMode ? `bg-darkMainColor` : `bg-white`} px-2 py-1.5 text-base placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 `}
                                                    >
                                                        <option value="">Select U/M...</option>
                                                        <option value="ACR">Acre</option>
                                                        <option value="B12">BAG 12#</option>
                                                        <option value="B22">BAG 22#</option>
                                                        <option value="B25">BAG 25#</option>
                                                        <option value="B30">BAG 30#</option>
                                                        <option value="B40">BAG 40#</option>
                                                        <option value="B5">BAG 5#</option>
                                                        <option value="B50">BAG 50#</option>
                                                        <option value="B51">BAG 51#</option>
                                                        <option value="BAG">BAG (Unspecified Weight)</option>
                                                        <option value="2.5">CASE (2x2.5)</option>
                                                        <option value="CS4">CASE (4X1)</option>
                                                        <option value="CAS">CASE</option>
                                                        <option value="30">DRUM 30-GAL</option>
                                                        <option value="55">DRUM 55-GAL</option>
                                                        <option value="DRU">DRUM (Unspecified Volume)</option>
                                                        <option value="EAC">EACH</option>
                                                        <option value="GAL">GALLON</option>
                                                        <option value="TR">TOTE REFILL</option>
                                                        <option value="LB">POUND</option>
                                                        <option value="OZ">Ounce - Av</option>
                                                        <option value="PAI">PAIL 5-GAL</option>
                                                        <option value="SAC">Super Sack (B1000)</option>
                                                        <option value="SAC2">Super Sack (B2000)</option>
                                                        <option value="TON">TON (2,000#)</option>
                                                        <option value="275">TOTE 275-GAL</option>
                                                    </Select>
                                                    
                                                    
                                                    <Select
                                                        type="text"
                                                        name="um"
                                                        value={price.um}
                                                        onChange={(e) => handleInputChange(index, 'um', e.target.value)}
                                                        className={`${BULK ? '' : 'hidden'} block w-full rounded-md ${darkMode ? `bg-darkMainColor` : `bg-white`} px-2 py-1.5 text-base placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 `}
                                                    >
                                                        <option value="">Select Rate...</option>
                                                        <option value="0.25ACR">1/4 Ton Per Acre</option>
                                                        <option value="0.5ACR">1/2 Ton Per Acre</option>
                                                        <option value="0.75ACR">3/4 Ton Per Acre</option>
                                                        <option value="1ACR">1 Ton Per Acre</option>
                                                        <option value="1.25ACR">1 1/4 Ton Per Acre</option>
                                                        <option value="1.5ACR">1 1/2 Ton Per Acre</option>
                                                        <option value="2ACR">2 Ton Per Acre</option>
                                                    </Select>
                                                    
                                                    
                                                </div>
                                                
                                                {/*Product Code*/}
                                                <div className={`relative col-span-1`}>
                                                    <Input
                                                        name="code"
                                                        type="string"
                                                        placeholder="Code..."
                                                        value={price.code}
                                                        onChange={(e) => handleInputChange(index, 'code', e.target.value)}
                                                        className={`block w-full text-gray-500 rounded-md ${darkMode ? 'bg-darkMainColor' : 'bg-white'} px-2 py-1 placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 `}
                                                    />
                                                </div>
                                                
                                                {/*NE Price*/}
                                                <div className={`relative col-span-1`}>
                                                    
                                                    <div className={`flex  gap-x-1.5 items-center w-full rounded-md ${darkMode ? 'bg-darkMainColor' : 'bg-white'} px-2 py-1 text-base placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 `}>
                                                        <div className="hidden lg:block text-borderColor text-xxs ">$</div>
                                                        <Input
                                                            type="number"
                                                            name="ne"
                                                            value={price.ne}
                                                            onChange={(e) => handleInputChange(index, 'ne', e.target.value)}
                                                            placeholder={`NE`}
                                                            className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white' : 'bg-white text-gray-900'} text-base placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 `}
                                                        />
                                                        <div className="hidden lg:block text-borderColor w-fit text-xxs ml-1">USD</div>
                                                    </div>
                                                </div>
                                                
                                                {/*SE Price*/}
                                                <div className={`relative col-span-1`}>
                                                    <div className={`flex  gap-x-1.5 items-center w-full rounded-md ${darkMode ? 'bg-darkMainColor' : 'bg-white'} px-2 py-1 text-base placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 `}>
                                                        <div className="hidden lg:block text-borderColor text-xxs ">$</div>

                                                        <Input
                                                            type="number"
                                                            name="se"
                                                            value={price.se}
                                                            onChange={(e) => handleInputChange(index, 'se', e.target.value)}
                                                            placeholder={`SE`}
                                                            className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white' : 'bg-white text-gray-900'} text-base placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 `}
                                                        />
                                                        <div className="hidden lg:block text-borderColor w-fit text-xxs ml-1">USD</div>


                                                    </div>
                                                </div>
                                                
                                                {/*AG Price*/}
                                                <div className={`relative col-span-1`}>
                                                    <div className={`flex  gap-x-1.5 items-center w-full rounded-md ${darkMode ? 'bg-darkMainColor' : 'bg-white'} px-2 py-1 text-base placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 `}>
                                                        <div className="hidden lg:block text-borderColor text-xxs ">$</div>

                                                        <Input
                                                            type="number"
                                                            name="ag"
                                                            value={price.ag}
                                                            onChange={(e) => handleInputChange(index, 'ag', e.target.value)}
                                                            placeholder={`AG`}
                                                            className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white' : 'bg-white text-gray-900'} text-base placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 `}
                                                        />
                                                        <div className="hidden lg:block text-borderColor w-fit text-xxs ml-1">USD</div>


                                                    </div>
                                                </div>
                                                
                                                {/*DIST1 Price*/}
                                                <div className={`relative col-span-1`}>
                                                    <div className={`flex  gap-x-1.5 items-center w-full rounded-md ${darkMode ? 'bg-darkMainColor' : 'bg-white'} px-2 py-1 text-base placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 `}>
                                                        <div className="hidden lg:block text-borderColor text-xxs ">$</div>

                                                        <Input
                                                            type="number"
                                                            name="dist1"
                                                            value={price.dist1}
                                                            onChange={(e) => handleInputChange(index, 'dist1', e.target.value)}
                                                            placeholder={`DIST 1`}
                                                            className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white' : 'bg-white text-gray-900'} text-base placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 `}
                                                        />
                                                        <div className="hidden lg:block text-borderColor w-fit text-xxs ml-1">USD</div>


                                                    </div>
                                                </div>
                                                
                                                {/*DIST2 Price*/}
                                                <div className={`relative col-span-1`}>
                                                    <div className={`flex  gap-x-1.5 items-center w-full rounded-md ${darkMode ? 'bg-darkMainColor' : 'bg-white'} px-2 py-1 text-base placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 `}>
                                                        <div className="hidden lg:block text-borderColor text-xxs ">$</div>

                                                        <Input
                                                            type="number"
                                                            name="dist2"
                                                            value={price.dist2}
                                                            onChange={(e) => handleInputChange(index, 'dist2', e.target.value)}
                                                            placeholder={`DIST 2`}
                                                            className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white' : 'bg-white text-gray-900'} text-base placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 `}
                                                        />
                                                        <div className="hidden lg:block text-borderColor w-fit text-xxs ml-1">USD</div>


                                                    </div>
                                                </div>
                                                
                                            </div>
                                            
                                            <Button
                                                className="hover:scale-105 hover:animate-pulse text-red-500 w-fit h-fit mt-auto mb-auto rounded-full mr-1"
                                                onClick={() => handleRemovePrice(index)}
                                            >
                                                <XCircleIcon className="size-5"/>
                                            </Button>
        
                                        </div>
                                        
                                        
                                    </div>
                            
                                    
                                )
                            })}
                        </div>
    
                    )}
                </div>

            </div>
            
            
            <Dialog open={isDeleting} onClose={setIsDeleting} className="relative z-50">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500/50 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
              />
        
              <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                  <DialogPanel
                    transition
                    className={`relative transform overflow-hidden rounded-lg ${darkMode ? 'bg-darkMainColor text-white' : 'bg-white text-black'} px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95`}
                  >
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-600/15 sm:mx-0 sm:size-10">
                        <TrashIcon aria-hidden="true" className="size-6 text-red-600" />
                      </div>
                      <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                        <DialogTitle as="h3" className="text-base font-semibold">
                          Delete | {product.name}
                        </DialogTitle>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">
                            Are you sure you want to delete this Product? Once you delete this product you will not be able to recover the data it will be permanently deleted from the server forever!
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <Button
                        type="button"
                        onClick={() => handleDeleteProduct(product.id)}
                        className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                      >
                        Delete
                      </Button>
                      <Button
                        type="button"
                        data-autofocus
                        onClick={() => setIsDeleting(false)}
                        className={`mt-3 inline-flex w-full justify-center rounded-md ${darkMode ? 'bg-darkBgColor' : 'bg-white ring-1 ring-inset ring-gray-300'} px-3 py-2 text-sm font-semibold shadow-sm hover:bg-opacity-50 sm:mt-0 sm:w-auto`}
                      >
                        Cancel
                      </Button>
                    </div>
                  </DialogPanel>
                </div>
              </div>
            </Dialog>
            
            
            <Dialog open={open} onClose={setOpen} className={`relative z-50 ${darkMode ? 'text-white' : 'text-black'}`}>
                
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500/20 transition-opacity duration-500 ease-in-out data-[closed]:opacity-0"
                />
                
                <div className="fixed inset-0 overflow-hidden">
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full h-full">
                            <DialogPanel
                                transition
                                className="pointer-events-auto w-screen max-w-md transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
                            >
                                <div className={`flex flex-col h-full ${darkMode ? 'bg-darkMainColor' : 'bg-white'} shadow-2xl`}>
                                    
                                    
                                    {/*Drawer Header*/}
                                    <div className={`${darkMode ? ' border-neutral-700' : ''} border-b-2 border-dashed px-4 py-4 sm:px-6`}>
                                        <div className="flex items-center justify-between">
                                            <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-green-600/15 sm:mx-0 sm:size-10">
                                                <DocumentCurrencyDollarIcon aria-hidden="true" className="size-5 text-green-600"/>
                                            </div>
                                    
                                            <DialogTitle className="text-base font-semibold tracking-widest">
                                                Product Options
                                            </DialogTitle>
                                    
                                            <div className="ml-3 flex h-7 items-center">
                                                <Button
                                                    type="button"
                                                    // onClick={clearForm}
                                                    className="relative rounded-md hover:text-red-500 hover:animate-heartbeat"
                                                >
                                                    <span className="absolute -inset-2.5"/>
                                                    <span className="sr-only">Clear Form</span>
                                                    <XCircleIcon aria-hidden="true" className="size-6"/>
                                                </Button>
                                            </div>
                                        </div>
                                    </div>

                                    
                                    
                                    {/*Body*/}
                                    <div className="relative p-3 flex flex-col overflow-y-scroll scrollbar-hide flex-1 space-y-8">
                                        
                                        
                                        {/*Fields*/}
                                        <div className="px-3 pt-6 w-full flex items-center">
                                            
                                            <div className={`grid grid-cols-1 w-full gap-y-4`}>
                                                
                                                {/*SDS*/}
                                                <div className={`relative col-span-1`}>
                                                    <label
                                                        className={`absolute flex -top-2 left-2 rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xxs font-medium`}
                                                    >
                                                        SDS
                                                    </label>
                                                    <Input
                                                        type="string"
                                                        placeholder="SDS"
                                                        value={sds}
                                                        onChange={handleSDSChange}
                                                        className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white outline-neutral-700 outline-2' : 'bg-white text-gray-900 outline-gray-300'} px-2 py-1 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-500
                    
                    
                     focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 `}
                                                    />
                                                </div>
                                                
                                                {/*PH*/}
                                                <div className={`relative col-span-1`}>
                                                    <label
                                                        className={`absolute flex -top-2 left-2 rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xxs font-medium`}
                                                    >
                                                        PH
                                                    </label>
                                                    <Input
                                                        type="string"
                                                        placeholder="pH"
                                                        value={ph}
                                                        onChange={handlePHChange}
                                                        className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white outline-neutral-700 outline-2' : 'bg-white text-gray-900 outline-gray-300'} px-2 py-1 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-500
                    
                    
                     focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 `}
                                                    />
                                                </div>
                                                
                                                {/*Weight*/}
                                                <div className={`relative col-span-1`}>
                                                    <label
                                                        className={`absolute flex -top-2 left-2 rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xxs font-medium`}
                                                    >
                                                        Weight
                                                    </label>
                                                    <Input
                                                        type="string"
                                                        placeholder="lbs per gal"
                                                        value={weight}
                                                        onChange={handleWeightChange}
                                                        className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white outline-neutral-700 outline-2' : 'bg-white text-gray-900 outline-gray-300'} px-2 py-1 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-500
                    
                    
                     focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 `}
                                                    />
                                                </div>
                                                
                                                {/*Gal Per Ton*/}
                                                <div className={`relative col-span-1`}>
                                                    <label
                                                        className={`absolute flex -top-2 left-2 rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xxs font-medium`}
                                                    >
                                                        Gallon/Ton
                                                    </label>
                                                    <Input
                                                        type="string"
                                                        placeholder="gal per ton"
                                                        value={GPT}
                                                        onChange={handleGPTChange}
                                                        className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white outline-neutral-700 outline-2' : 'bg-white text-gray-900 outline-gray-300'} px-2 py-1 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 `}
                                                    />
                                                </div>
                                                
                                            </div>
                    
                                        </div>
                                        

                                        {/*Price Lock*/}
                                        <div className={`flex flex-col space-y-1`}>
                                            
                                            {/*Header*/}
                                            <div className={`flex px-4`}>
                                                <div className={`text-sm tracking-tighter`}>Price Lock</div>
                                                
                                                <Field className="flex items-center ml-3">
                                                    <Switch
                                                        checked={lock}
                                                        onChange={(value) => {
                                                            setLock(value);
                                                            setIsModified(true); // Set isModified to true when lock changes
                                                        }}
                                                        className="group relative inline-flex h-5 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-transparent"
                                                    >
                                                        <span className="sr-only">Use setting</span>
                                                        <span aria-hidden="true"
                                                              className="pointer-events-none absolute size-full rounded-md"/>
                                                        <span
                                                            aria-hidden="true"
                                                            className={`pointer-events-none absolute mx-auto h-3 w-5 rounded-full ${darkMode ? 'bg-neutral-700' : 'bg-gray-200'} transition-colors duration-200 ease-in-out group-data-[checked]:bg-green-600`}
                                                        />
                                                        <span
                                                            aria-hidden="true"
                                                            className="pointer-events-none absolute left-0 inline-block size-4 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out group-data-[checked]:translate-x-5"
                                                        />
                                                    </Switch>
                                                    <Label as="span" className={`ml-3 text-sm ${darkMode ? 'text-white' : 'text-black'}`}>
                                                        <span className="font-medium text-sm text-gray-500 tracking-tighter">{lock ? "Locked" : "Unlocked"}</span>
                                                    </Label>
                                                </Field>
                                            </div>
                                            
                                            {/*Description*/}
                                            <div className={`px-8 text-xxs text-gray-500`}>This settings locks the price of this product. No discount may be applied and price may not be edited.</div>
                                        
                                        </div>
                                        
                                        {/*AG*/}
                                        <div className={`flex flex-col space-y-1`}>
                                            
                                            {/*Header*/}
                                            <div className={`flex px-4`}>
                                                <div className={`text-sm tracking-tighter`}>AG Product</div>
                                                
                                                <Field className="flex items-center ml-3">
                                                    <Switch
                                                        checked={AG}
                                                        onChange={(value) => {
                                                            setAG(value);
                                                            setIsModified(true); // Set isModified to true when lock changes
                                                        }}
                                                        className="group relative inline-flex h-5 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-transparent"
                                                    >
                                                        <span className="sr-only">Use setting</span>
                                                        <span aria-hidden="true"
                                                              className="pointer-events-none absolute size-full rounded-md"/>
                                                        <span
                                                            aria-hidden="true"
                                                            className={`pointer-events-none absolute mx-auto h-3 w-5 rounded-full ${darkMode ? 'bg-neutral-700' : 'bg-gray-200'} transition-colors duration-200 ease-in-out group-data-[checked]:bg-green-600`}
                                                        />
                                                        <span
                                                            aria-hidden="true"
                                                            className="pointer-events-none absolute left-0 inline-block size-4 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out group-data-[checked]:translate-x-5"
                                                        />
                                                    </Switch>
                                                    <Label as="span" className={`ml-3 text-sm ${darkMode ? 'text-white' : 'text-black'}`}>
                                                        <span className="font-medium text-sm text-gray-500 tracking-tighter">{AG ? "AG Exclusive" : ""}</span>
                                                    </Label>
                                                </Field>
                                            </div>
                                            
                                            {/*Description*/}
                                            <div className={`px-8 text-xxs text-gray-500`}>This settings sets the price to be an AG product only. This will make it appear only for AG customers. This is also for products that have a -AG after the code.</div>
                                        </div>
                                        
                                        {/*PLAIF*/}
                                        <div className={`flex flex-col space-y-1`}>
                                            
                                            {/*Header*/}
                                            <div className={`flex px-4`}>
                                                <div className={`text-sm tracking-tighter`}>PLAIF Product</div>
                                                
                                                <Field className="flex items-center ml-3">
                                                    <Switch
                                                        checked={PLAIF}
                                                        onChange={(value) => {
                                                            setPLAIF(value);
                                                            setIsModified(true); // Set isModified to true when lock changes
                                                        }}
                                                        className="group relative inline-flex h-5 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-transparent"
                                                    >
                                                        <span className="sr-only">Use setting</span>
                                                        <span aria-hidden="true"
                                                              className="pointer-events-none absolute size-full rounded-md"/>
                                                        <span
                                                            aria-hidden="true"
                                                            className={`pointer-events-none absolute mx-auto h-3 w-5 rounded-full ${darkMode ? 'bg-neutral-700' : 'bg-gray-200'} transition-colors duration-200 ease-in-out group-data-[checked]:bg-green-600`}
                                                        />
                                                        <span
                                                            aria-hidden="true"
                                                            className="pointer-events-none absolute left-0 inline-block size-4 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out group-data-[checked]:translate-x-5"
                                                        />
                                                    </Switch>
                                                    <Label as="span" className={`ml-3 text-sm ${darkMode ? 'text-white' : 'text-black'}`}>
                                                        <span className="font-medium text-sm text-gray-500 tracking-tighter">{PLAIF ? "PLAIF Exclusive" : ""}</span>
                                                    </Label>
                                                </Field>
                                            </div>
                                            
                                            {/*Description*/}
                                            <div className={`px-8 text-xxs text-gray-500`}>This settings locks the product as a PLAIF product. This is for products that have a -PLAIF after the code.</div>
                                        
                                        </div>
                                        
                                        {/*Website*/}
                                        <div className={`flex flex-col space-y-1`}>
                                            
                                            {/*Header*/}
                                            <div className={`flex px-4`}>
                                                <div className={`text-sm tracking-tighter`}>Website Product</div>
                                                
                                                <Field className="flex items-center ml-3">
                                                    <Switch
                                                        checked={WEB}
                                                        disabled={true}
                                                        onChange={(value) => {
                                                            setWEB(value);
                                                            setIsModified(true); // Set isModified to true when lock changes
                                                        }}
                                                        className="group relative inline-flex h-5 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-transparent"
                                                    >
                                                        <span className="sr-only">Use setting</span>
                                                        <span aria-hidden="true"
                                                              className="pointer-events-none absolute size-full rounded-md"/>
                                                        <span
                                                            aria-hidden="true"
                                                            className={`pointer-events-none absolute mx-auto h-3 w-5 rounded-full ${darkMode ? 'bg-neutral-700' : 'bg-gray-200'} transition-colors duration-200 ease-in-out group-data-[checked]:bg-green-600`}
                                                        />
                                                        <span
                                                            aria-hidden="true"
                                                            className="pointer-events-none absolute left-0 inline-block size-4 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out group-data-[checked]:translate-x-5"
                                                        />
                                                    </Switch>
                                                    <Label as="span" className={`ml-3 text-sm ${darkMode ? 'text-white' : 'text-black'}`}>
                                                        <span className="font-medium text-sm text-gray-500 tracking-tighter">{WEB ? "Displayed" : "Hidden"}</span>
                                                    </Label>
                                                </Field>
                                            </div>
                                            
                                            {/*Description*/}
                                            <div className={`px-8 text-xxs text-gray-500`}>This settings determines if the product will be displayed on the website. You may hide and display the product at any time.</div>
                                        
                                        </div>
                                        
                                        
                                        {/*BULK*/}
                                        <div className={`flex flex-col space-y-1`}>
                                            
                                            {/*Header*/}
                                            <div className={`flex px-4`}>
                                                <div className={`text-sm tracking-tighter`}>Bulk Application Item</div>
                                                
                                                <Field className="flex items-center ml-3">
                                                    <Switch
                                                        checked={BULK}
                                                        disabled={false}
                                                        onChange={(value) => {
                                                            setBulk(value);
                                                            setIsModified(true); // Set isModified to true when lock changes
                                                        }}
                                                        className="group relative inline-flex h-5 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-transparent"
                                                    >
                                                        <span className="sr-only">Use setting</span>
                                                        <span aria-hidden="true"
                                                              className="pointer-events-none absolute size-full rounded-md"/>
                                                        <span
                                                            aria-hidden="true"
                                                            className={`pointer-events-none absolute mx-auto h-3 w-5 rounded-full ${darkMode ? 'bg-neutral-700' : 'bg-gray-200'} transition-colors duration-200 ease-in-out group-data-[checked]:bg-green-600`}
                                                        />
                                                        <span
                                                            aria-hidden="true"
                                                            className="pointer-events-none absolute left-0 inline-block size-4 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out group-data-[checked]:translate-x-5"
                                                        />
                                                    </Switch>
                                                    
                                                </Field>
                                            </div>
                                            
                                            {/*Description*/}
                                            <div className={`px-8 text-xxs text-gray-500`}>This settings determines if the product is a bulk application item. This will change the UM in the pricing table to RATE</div>
                                        
                                        </div>
                                    
                                    
                                    </div>
                                
                                
                                </div>
                            </DialogPanel>
                        </div>
                    </div>
                </div>
            </Dialog>
        
        
        </div>
    )
}

export default PricingCard;
